import { useState } from 'react';

interface HeroProps {
  scrolled: boolean;
}

export default function Hero({ scrolled }: HeroProps) {
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitStatus('Sending...');
    
    const form = e.currentTarget;
    const formData = new FormData(form);
    const data = new URLSearchParams();
    
    formData.forEach((value, key) => {
      data.append(key, value.toString());
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/d5e5oendcu97hjrn4df0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: data.toString(),
      });

      if (response.ok) {
        setSubmitStatus('Thank you! We will contact you soon.');
        form.reset();
        setTimeout(() => {
          setShowQuoteModal(false);
          setSubmitStatus('');
        }, 2000);
      } else {
        setSubmitStatus('Error sending request. Please try again.');
      }
    } catch (error) {
      setSubmitStatus('Error sending request. Please try again.');
    }
  };

  return (
    <section className="relative h-screen w-full overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/c16545f65908f84f85c64990fcdff677.jpg"
          alt="Fine Frank Home Improvement"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
      </div>

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-lg' : 'bg-black/20'}`}>
        <div className="w-full px-8 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <img 
                src="https://static.readdy.ai/image/9b35b8180c803dd171897861355a5e85/b784f0575a6f7e3110426d72ffd8aa87.jpeg" 
                alt="Fine Frank Home Improvement Logo" 
                className="h-36 w-36 object-contain"
              />
              <div className="flex flex-col">
                <span className="text-[3.375rem] font-sans font-bold text-gray-900 leading-none">
                  Fine Frank
                </span>
                <span className="text-[2.36rem] font-sans font-semibold text-gray-900 leading-none">
                  Home Improvement
                </span>
              </div>
            </div>

            {/* Navigation Links */}
            <div className="flex items-center gap-8">
              <a href="#home" className={`text-sm font-light transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white/90 hover:text-white'}`}>
                Home
              </a>
              <a href="#services" className={`text-sm font-light transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white/90 hover:text-white'}`}>
                Services
              </a>
              <a href="#portfolio" className={`text-sm font-light transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white/90 hover:text-white'}`}>
                Portfolio
              </a>
              <a href="#cabinets" className={`text-sm font-light transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white/90 hover:text-white'}`}>
                Cabinets
              </a>
              <a href="#contact" className={`text-sm font-light transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white/90 hover:text-white'}`}>
                Contact
              </a>
              <button 
                onClick={() => setShowQuoteModal(true)}
                className="px-6 py-2.5 bg-[#FF6B4A] text-gray-900 text-sm font-semibold rounded-full hover:bg-[#ff5a39] transition-colors whitespace-nowrap cursor-pointer"
              >
                Get Free Quote
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Content */}
      <div className="relative z-10 h-full flex flex-col justify-center px-16">
        <div className="max-w-7xl">
          <h1 className="text-5xl font-bold text-white mb-6 leading-tight mt-24">
            <div className="font-sans">Professional Home</div>
            <div className="font-sans">Improvement & Construction</div>
          </h1>
          <p className="text-2xl text-white/90 font-light mb-12">
            Transforming Houses into Dream Homes
          </p>

          <div className="flex gap-8 mt-16">
            <div className="bg-black/30 backdrop-blur-sm px-6 py-4 rounded-lg">
              <p className="text-white text-base font-light">
                Expert handyman services, residential & commercial construction
              </p>
            </div>
            <div className="bg-black/30 backdrop-blur-sm px-6 py-4 rounded-lg mt-8">
              <p className="text-white text-base font-light">
                Custom cabinet manufacturing through Fine Frank Cabinets
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Quote Modal */}
      {showQuoteModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setShowQuoteModal(false)}>
          <div className="bg-white rounded-2xl p-8 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900">Get Free Quote</h3>
              <button onClick={() => setShowQuoteModal(false)} className="text-gray-500 hover:text-gray-700 cursor-pointer">
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>
            <form className="space-y-4" data-readdy-form id="quote-form" onSubmit={handleSubmit}>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input type="text" name="name" required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B4A] focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input type="email" name="email" required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B4A] focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input type="tel" name="phone" required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B4A] focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Service Needed</label>
                <select name="service" required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B4A] focus:border-transparent">
                  <option value="">Select a service</option>
                  <option value="Handyman Services">Handyman Services</option>
                  <option value="Residential Construction">Residential Construction</option>
                  <option value="Commercial Construction">Commercial Construction</option>
                  <option value="Fine Frank Cabinets">Fine Frank Cabinets</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Project Details</label>
                <textarea name="details" rows={4} maxLength={500} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B4A] focus:border-transparent resize-none"></textarea>
                <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
              </div>
              {submitStatus && (
                <p className={`text-sm ${submitStatus.includes('Error') ? 'text-red-600' : 'text-green-600'}`}>
                  {submitStatus}
                </p>
              )}
              <button type="submit" className="w-full bg-[#FF6B4A] text-white py-3 rounded-lg font-semibold hover:bg-[#ff5a39] transition-colors cursor-pointer whitespace-nowrap">
                Submit Request
              </button>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}
